import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MenuProductSkeleton } from 'features/menu/components/menu-product-skeleton';
import {
  ProductHomeInnerLayout,
  ProductHomeLayoutContainer,
  ProductInformationSection,
} from 'features/menu/components/product-detail/product-home/styled';
import { ProductInformation } from 'features/menu/components/product-detail/product-information';
import { ProductNutritionInformation } from 'features/menu/components/product-detail/product-nutrition-information/product-nutrition-information';
import { CurvedBackground } from 'features/menu/components/product-detail/product-overview/curved-bg';
import {
  ProductImage,
  ProductOverviewContainer,
} from 'features/menu/components/product-detail/product-overview/styled';
import { Animated, LayoutChangeEvent, StyleSheet, View } from 'react-native';
import { Icon } from 'ucl/components/icon/Icon';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { CatalogComboSlotsSection } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-catalog-preview/catalog-combo-slots-section';
import { CatalogPriceCalories } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-catalog-preview/catalog-price-calories';
import {
  buildCatalogIndexedMenu,
  getCatalogSelectedNutritionItems,
} from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-catalog-preview/get-catalog-included';
import { asPreviewMenuEntry } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/as-preview-menu-entry';
import { OneMenuCatalogEntry } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-one-menu-catalog';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { IIngredients, IndexedMenu } from 'state/unified-menu/types';
import { IUnifiedMenuContext, UnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { mapImageResourceToSanityImage } from 'state/unified-menu/utils';
import { primitive } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { WEB_APP_CTA_MAX_WIDTH } from 'utils/constants';
import { hapticImpact } from 'utils/haptic';

type MenuCatalogPreviewProps = {
  entry?: OneMenuCatalogEntry;
  entries?: readonly OneMenuCatalogEntry[];
  ingredients?: IIngredients[];
  isLoading?: boolean;
  /** Route type used by the same product skeleton as the store-selected PDP. */
  skeletonType?: string | null;
  onSelectRestaurant: () => void;
};

/** No-store menu deeplink preview: product + info sections, then store CTA. */
export function MenuCatalogPreview({
  entry,
  entries = [],
  ingredients = [],
  isLoading = false,
  skeletonType = null,
  onSelectRestaurant,
}: MenuCatalogPreviewProps) {
  const { language } = useLocale();
  const isMobile = useIsMobileBreakpoint();
  const selectedProduct = entry ? asPreviewMenuEntry(entry, language) : undefined;
  const name = selectedProduct?.name?.locale || '';
  const imageResource = selectedProduct?.image?.resource;
  const imageAlt = selectedProduct?.image?.altText?.locale || name;
  const image = imageResource ? mapImageResourceToSanityImage(imageResource, imageAlt) : undefined;
  const [bgHeight, setBgHeight] = useState(0);
  const [ctaHighlighted, setCtaHighlighted] = useState(false);
  const ctaScale = useRef(new Animated.Value(1)).current;
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const catalogMenu = useMemo(
    () => buildCatalogIndexedMenu(entries.length ? entries : entry ? [entry] : [], language),
    [entries, entry, language]
  );

  const selectedNutritionItems = useMemo(
    () => (selectedProduct ? getCatalogSelectedNutritionItems(catalogMenu, selectedProduct) : []),
    [catalogMenu, selectedProduct]
  );

  const highlightSelectRestaurant = useCallback(() => {
    void hapticImpact();
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    setCtaHighlighted(true);
    ctaScale.setValue(1);
    Animated.sequence([
      Animated.timing(ctaScale, { toValue: 1.04, duration: 120, useNativeDriver: true }),
      Animated.timing(ctaScale, { toValue: 1, duration: 120, useNativeDriver: true }),
      Animated.timing(ctaScale, { toValue: 1.04, duration: 120, useNativeDriver: true }),
      Animated.timing(ctaScale, { toValue: 1, duration: 120, useNativeDriver: true }),
    ]).start();
    highlightTimeoutRef.current = setTimeout(() => {
      setCtaHighlighted(false);
      highlightTimeoutRef.current = null;
    }, 1200);
  }, [ctaScale]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const unifiedMenuValue = useMemo<IUnifiedMenuContext>(
    () => ({
      activeDayParts: [],
      dayParts: [],
      rawMenu: undefined,
      indexedMenu: catalogMenu,
      indexedRewards: IndexedMenu.EMPTY,
      indexedOffers: IndexedMenu.EMPTY,
      loadedStoreNumber: null,
      mainMenu: [],
      // Preview has no store day-part source; treat as resolved with empty parts.
      isDayPartsSourceResolved: true,
      isLoading: false,
      hasError: false,
      refetchMenu: async () => undefined,
      ingredients,
    }),
    [catalogMenu, ingredients]
  );

  const showProduct = !isLoading && !!selectedProduct;

  return (
    <UnifiedMenuContext.Provider value={unifiedMenuValue}>
      <CardModal
        alwaysShowHeader
        headerText={showProduct ? name : undefined}
        isLoading={!showProduct}
        scrollView={showProduct}
        // Desktop web otherwise caps this body at 560px. The curve is full-bleed on
        // the production PDP, so a capped column leaves a white panel beside it.
        noDesktopWebMaxWidth
        // Match PDP ProductHome: white below the mayo curve so it meets the product content.
        scrollContainerStyle={styles.scrollContent}
        wrappedChildrenStyle={styles.whiteSurface}
        footer={
          !showProduct ? undefined : (
            <View style={styles.footer}>
              <Animated.View style={{ transform: [{ scale: ctaScale }], width: '100%' }}>
                <ActionButton
                  testID="menu-catalog-preview-select-restaurant"
                  fullWidth
                  // Pulse to PRIMARY briefly when a combo slot is tapped.
                  variant={
                    ctaHighlighted ? ActionButtonVariants.PRIMARY : ActionButtonVariants.OUTLINE
                  }
                  onPress={onSelectRestaurant}
                  leftIcon={
                    <Icon
                      name="mi:location-on"
                      width="24px"
                      {...hiddenAccessibilityPlatformProps}
                    />
                  }
                >
                  {t('selectRestaurantToContinue')}
                </ActionButton>
              </Animated.View>
            </View>
          )
        }
      >
        {showProduct && selectedProduct ? (
          <View style={styles.content} testID="menu-catalog-preview">
            {image ? (
              <ProductOverviewContainer withSpacing={false} style={styles.overview}>
                <View style={[styles.curvedBackground, { height: bgHeight }]}>
                  <CurvedBackground
                    height={bgHeight}
                    curveColor={primitive.$white}
                    backgroundColor={primitive.$mayoEggWhite}
                  />
                </View>
                <ProductImage
                  image={image}
                  alt={imageAlt}
                  onLayout={({ nativeEvent }: LayoutChangeEvent) => {
                    const { height } = nativeEvent.layout;
                    setBgHeight(height - height * 0.4);
                  }}
                />
                <CatalogPriceCalories product={selectedProduct} />
              </ProductOverviewContainer>
            ) : (
              <CatalogPriceCalories product={selectedProduct} />
            )}

            <CatalogComboSlotsSection
              menu={catalogMenu}
              product={selectedProduct}
              onRequireRestaurantSelection={highlightSelectRestaurant}
            />

            <ProductHomeLayoutContainer
              isFullContainer={isMobile}
              backgroundColor={primitive.$mayoEggWhite}
            >
              <ProductHomeInnerLayout $isFullContainer={isMobile}>
                <ProductInformationSection>
                  {selectedProduct.description?.locale ? (
                    <ProductInformation selectedProduct={selectedProduct} />
                  ) : null}

                  <ProductNutritionInformation
                    selectedProduct={selectedProduct}
                    nutritionItems={selectedNutritionItems}
                  />
                </ProductInformationSection>
              </ProductHomeInnerLayout>
            </ProductHomeLayoutContainer>
          </View>
        ) : (
          <MenuProductSkeleton hideHeader type={skeletonType} />
        )}
      </CardModal>
    </UnifiedMenuContext.Provider>
  );
}

const styles = StyleSheet.create({
  content: {
    alignSelf: 'stretch',
    backgroundColor: primitive.$white,
    flexGrow: 1,
    width: '100%',
  },
  curvedBackground: {
    left: 0,
    position: 'absolute',
    right: 0,
    width: '100%',
  },
  footer: {
    maxWidth: WEB_APP_CTA_MAX_WIDTH,
    width: '100%',
  },
  overview: {
    alignSelf: 'stretch',
    width: '100%',
  },
  scrollContent: {
    alignItems: 'stretch',
    backgroundColor: primitive.$white,
    flexGrow: 1,
    width: '100%',
  },
  whiteSurface: {
    alignSelf: 'stretch',
    backgroundColor: primitive.$white,
    flexGrow: 1,
    width: '100%',
  },
});
